<template>
  <div class="visit-stats-card card-base card-shadow--small mb-20" v-loading="loading">
    <div class="vsc-header">
      <i class="mdi mdi-calendar-check-outline vsc-title-icon"></i>
      <span class="vsc-title">{{ $t('dashboards.visitStats.title') }}</span>
    </div>

    <div class="vsc-kpis">
      <button type="button" class="vsc-kpi vsc-kpi--planned" @click="openPlannedVisits">
        <div class="vsc-kpi__head">
          <i class="mdi mdi-calendar-clock-outline vsc-kpi__icon"></i>
          <span class="vsc-kpi__value">{{ stats.plannedCount }}</span>
        </div>
        <div class="vsc-kpi__label">{{ $t('clients.visitsList.plannedKpiLabel') }}</div>
        <div class="vsc-kpi__hint">{{ $t('clients.visitsList.currentWeekPeriod') }}</div>
      </button>

      <button type="button" class="vsc-kpi vsc-kpi--reported" @click="openExecutedVisits">
        <div class="vsc-kpi__head">
          <i class="mdi mdi-clipboard-check-outline vsc-kpi__icon"></i>
          <span class="vsc-kpi__value">{{ stats.reportedCount }}</span>
        </div>
        <div class="vsc-kpi__label">{{ $t('clients.visitsList.savedReportsLabel') }}</div>
        <div class="vsc-kpi__hint">{{ $t('clients.visitsList.currentWeekPeriod') }}</div>
      </button>

      <div class="vsc-kpi vsc-kpi--rate">
        <div class="vsc-kpi__head">
          <i class="mdi mdi-chart-line vsc-kpi__icon"></i>
          <span class="vsc-kpi__value">{{ stats.completionRate }}%</span>
        </div>
        <div class="vsc-kpi__label">{{ $t('clients.visitsList.coverageRateLabel') }}</div>
        <div class="vsc-kpi__hint">{{ $t('clients.visitsList.reportsOverPlannedHint') }}</div>
      </div>
    </div>

    <div class="vsc-types">
      <div class="vsc-types__title">{{ $t('clients.visitsList.visitTypeShareTitle') }}</div>
      <div v-if="typeKpis.length === 0" class="vsc-types__empty">{{ $t('clients.visitsList.noVisitsForFilter') }}</div>
      <div v-else class="vsc-types__rows">
        <div v-for="typeKpi in typeKpis" :key="typeKpi.code" class="vsc-type-row">
          <span class="vsc-type-row__left">
            <i :class="typeKpi.icon"></i>
            <span>{{ typeKpi.label }}</span>
          </span>
          <span class="vsc-type-row__right">{{ typeKpi.count }} | {{ typeKpi.percentage }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import VisitAPI from '@/services/API/visit';

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

const VISIT_DOMAINS = [
  { code: 'VISIT', labelKey: 'clients.visitTypeLabels.visit', icon: 'mdi mdi-office-building' },
  { code: 'QUICK_CALL', labelKey: 'clients.visitTypeLabels.quickCall', icon: 'mdi mdi-phone-fast' },
  { code: 'PHONE_CALL', labelKey: 'clients.visitTypeLabels.phoneCall', icon: 'mdi mdi-phone' },
  { code: 'EMAIL-SMS', labelKey: 'clients.visitTypeLabels.emailSms', icon: 'mdi mdi-email-outline' },
  { code: 'DELIVERY-PICK_UP', labelKey: 'clients.visitTypeLabels.deliveryPickUp', icon: 'mdi mdi-truck-delivery-outline' },
  { code: 'DEGUSTATION', labelKey: 'clients.visitTypeLabels.degustation', icon: 'mdi mdi-glass-wine' },
  { code: 'OTHER', labelKey: 'clients.visitTypeLabels.other', icon: 'mdi mdi-shape-outline' },
];

export default {
  name: 'VisitStatsCard',
  props: {
    repId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      stats: {
        plannedCount: 0,
        reportedCount: 0,
        completionRate: 0,
      },
      typeKpis: [],
    };
  },
  watch: {
    repId() {
      this.fetchData();
    },
  },
  methods: {
    openExecutedVisits() {
      this.$router.push({
        path: '/visits',
        query: {
          tab: 'all',
          period: 'WEEK',
        },
      }).catch(() => {
        // Ignore duplicate navigation errors.
      });
    },
    openPlannedVisits() {
      this.$router.push({
        path: '/visits',
        query: {
          tab: 'planned',
          period: 'WEEK',
        },
      }).catch(() => {
        // Ignore duplicate navigation errors.
      });
    },
    normalizeVisitList(visitsResponse) {
      if (Array.isArray(visitsResponse)) return visitsResponse;
      if (visitsResponse && Array.isArray(visitsResponse.items)) return visitsResponse.items;
      if (visitsResponse && Array.isArray(visitsResponse.results)) return visitsResponse.results;
      if (visitsResponse && Array.isArray(visitsResponse.list)) return visitsResponse.list;
      if (visitsResponse && Array.isArray(visitsResponse.rows)) return visitsResponse.rows;
      if (visitsResponse && Array.isArray(visitsResponse.data)) return visitsResponse.data;
      if (visitsResponse && visitsResponse.data && Array.isArray(visitsResponse.data.list)) return visitsResponse.data.list;
      if (visitsResponse && visitsResponse.data && Array.isArray(visitsResponse.data.items)) return visitsResponse.data.items;
      return [];
    },
    normalizeRepId(visit) {
      return String(
        (visit && visit.rep && visit.rep.id)
        || (visit && visit.repId)
        || (visit && visit.rep_id)
        || (visit && visit.fk_rep_id)
        || (visit && visit.userId)
        || ''
      );
    },
    isWithinCurrentWeek(dateValue) {
      if (dateValue == null || dateValue === '') return false;
      const date = dayjs(dateValue);
      if (!date.isValid()) return false;
      const startOfWeek = dayjs().startOf('isoWeek');
      const endOfWeek = dayjs().endOf('isoWeek');
      return date.isBetween(startOfWeek, endOfWeek, null, '[]');
    },
    getVisitStatus(visit) {
      return String((visit && visit.status) || '').toUpperCase();
    },
    isPlannedVisit(visit) {
      return this.getVisitStatus(visit) === 'PLANNED';
    },
    getVisitDate(visit) {
      return (visit && (visit.date || visit.visitDate || visit.visitedAt || visit.created_at)) || null;
    },
    getPlannedVisitDate(visit) {
      if (this.isPlannedVisit(visit)) {
        return this.getVisitDate(visit);
      }
      return (visit && (visit.nextVisitDate || visit.next_visit_date)) || null;
    },
    getVisitId(visit) {
      if (!visit) return null;
      return visit.id || visit.visitId || visit.visit_id || null;
    },
    getLinkedPlannedVisitId(visit) {
      if (!visit) return null;
      return (visit.plannedVisit && visit.plannedVisit.id)
        || (visit.planned_visit && visit.planned_visit.id)
        || visit.plannedVisitId
        || visit.fk_planned_visit_id
        || null;
    },
    toComparableId(value) {
      if (value == null || value === '') {
        return null;
      }
      return String(value);
    },
    getVisitTypeCode(visit) {
      if (!visit) return 'OTHER';
      return (visit.visitType && visit.visitType.code)
        || (visit.visit_type && visit.visit_type.code)
        || (visit.type && visit.type.code)
        || visit.typeCode
        || visit.visitTypeCode
        || visit.visit_type_code
        || visit.type
        || 'OTHER';
    },
    visitTypeLabel(code) {
      const domain = VISIT_DOMAINS.find((item) => item.code === code);
      return domain ? this.$t(domain.labelKey) : code;
    },
    getVisitTypeIcon(code) {
      const domain = VISIT_DOMAINS.find((item) => item.code === code);
      return domain ? domain.icon : 'mdi mdi-shape-outline';
    },
    async fetchData() {
      this.loading = true;
      try {
        const visitsResponse = await VisitAPI.fetchVisits({ paging: 'no' });
        const visits = this.normalizeVisitList(visitsResponse).filter((visit) => visit != null && typeof visit === 'object');

        const repIdFilter = this.repId != null ? String(this.repId) : null;
        const filteredVisits = repIdFilter == null
          ? visits
          : visits.filter((visit) => this.normalizeRepId(visit) === repIdFilter);

        const plannedVisitsForWeek = filteredVisits.filter((visit) => {
          return this.isPlannedVisit(visit) && this.isWithinCurrentWeek(this.getPlannedVisitDate(visit));
        });

        const plannedVisitIds = new Set(
          plannedVisitsForWeek
            .map((visit) => this.toComparableId(this.getVisitId(visit)))
            .filter((id) => id != null)
        );

        const linkedReportedPlannedIds = new Set();
        filteredVisits.forEach((visit) => {
          if (this.isPlannedVisit(visit)) {
            return;
          }
          const linkedPlannedId = this.toComparableId(this.getLinkedPlannedVisitId(visit));
          if (linkedPlannedId != null && plannedVisitIds.has(linkedPlannedId)) {
            linkedReportedPlannedIds.add(linkedPlannedId);
          }
        });

        const plannedCount = plannedVisitsForWeek.length;
        const reportedCount = linkedReportedPlannedIds.size;
        const completionRate = plannedCount > 0 ? Math.round((reportedCount / plannedCount) * 100) : 0;

        const typeKpis = VISIT_DOMAINS
          .map((domain) => {
            const count = plannedVisitsForWeek.filter((visit) => this.getVisitTypeCode(visit) === domain.code).length;
            return {
              code: domain.code,
              label: this.visitTypeLabel(domain.code),
              icon: this.getVisitTypeIcon(domain.code),
              count,
              percentage: plannedCount > 0 ? Math.round((count / plannedCount) * 100) : 0,
            };
          })
          .filter((typeKpi) => typeKpi.count > 0)
          .sort((left, right) => {
            if (left.count === right.count) {
              return left.label.localeCompare(right.label, this.$i18n.locale);
            }
            return right.count - left.count;
          });

        this.stats = { plannedCount, reportedCount, completionRate };
        this.typeKpis = typeKpis;
      } catch (e) {
        console.error('VisitStatsCard error:', e);
        this.stats = { plannedCount: 0, reportedCount: 0, completionRate: 0 };
        this.typeKpis = [];
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
.visit-stats-card {
  padding: 16px;
  min-width: 360px;

  .vsc-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    .vsc-title-icon {
      font-size: 20px;
      color: #909399;
    }

    .vsc-title {
      font-weight: bold;
      font-size: 14px;
      color: #303133;
      flex: 1;
    }
  }

  .vsc-kpis {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }

  .vsc-kpi {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 10px;
    background: #fff;
    text-align: left;
    min-height: 98px;
  }

  button.vsc-kpi {
    cursor: pointer;
  }

  button.vsc-kpi:hover .vsc-kpi__value,
  button.vsc-kpi:hover .vsc-kpi__label {
    text-decoration: underline;
  }

  .vsc-kpi__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .vsc-kpi__icon {
    font-size: 18px;
    color: #606266;
  }

  .vsc-kpi__value {
    font-size: 22px;
    font-weight: 700;
    line-height: 1;
    color: #303133;
  }

  .vsc-kpi__label {
    font-size: 12px;
    color: #303133;
    font-weight: 600;
    line-height: 1.2;
  }

  .vsc-kpi__hint {
    font-size: 11px;
    color: #909399;
    margin-top: 4px;
  }

  .vsc-types {
    border-top: 1px solid #ebeef5;
    padding-top: 10px;
  }

  .vsc-types__title {
    font-size: 12px;
    font-weight: 700;
    color: #606266;
    margin-bottom: 6px;
  }

  .vsc-types__empty {
    font-size: 12px;
    color: #909399;
  }

  .vsc-types__rows {
    display: grid;
    gap: 4px;
  }

  .vsc-type-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #606266;
  }

  .vsc-type-row__left {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .vsc-type-row__right {
    color: #909399;
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    .vsc-kpis {
      grid-template-columns: 1fr;
    }
  }
}
</style>
